// src/components/SideRail.jsx
// The icon strip on the left (3.39.0, desktop): the pages people open now
// and then, grouped (Activity: Logs, Usage · People & settings: Users,
// Settings · Super Admin: Console · Help: Library; later themes, health and
// the compliance report; 3.40.0: People & settings first, Help is now
// Resources). Icons only; resting the mouse on it (or
// moving into it with the keyboard) slides out each page's name, and a
// click opens the page. It sits under the fixed top bar (top = its height).
// Phones use the ☰ menu in MainNav instead.
import { useEffect, useRef, useState } from 'react';
import Ambient from './Ambient';
import Emo from './Emo';

// 3.53.0: forceOpen keeps it slid open (the guided tour points at it)
export default function SideRail({ tabs, active, hrefFor, onGo, labelFor, alerts = {}, top = 0, forceOpen = false }) {
  const [hoverOpen, setOpen] = useState(false);
  const open = hoverOpen || forceOpen;
  const timer = useRef(null);
  useEffect(() => () => clearTimeout(timer.current), []);
  useEffect(() => { setOpen(false); }, [active]);
  const later = (v, ms) => { clearTimeout(timer.current); timer.current = setTimeout(() => setOpen(v), ms); };
  if (!tabs.length) return null;
  return (
    <nav aria-label="More pages" data-tour="left-rail" className="cp-ambient-host cp-night" style={{ ...S.rail, top, zIndex: open ? 101 : 95, width: open ? 184 : RAIL_WIDTH, boxShadow: open ? '6px 0 24px rgb(var(--c-000000-rgb) / 0.12)' : 'none' }}
      onMouseEnter={() => later(true, 120)} onMouseLeave={() => later(false, 180)}
      onFocus={() => later(true, 0)} onBlur={e => { if (!e.currentTarget.contains(e.relatedTarget)) later(false, 0); }}>
      <Ambient sheets={3} />   {/* 3.40.2: soft moving background, paper sheets drifting up */}
      <div style={S.hint} className="rail-muted" aria-hidden="true">
        <span style={S.chev} className="rail-chev">{open ? '«' : '»'}</span>
        <span style={{ ...S.label, opacity: open ? 1 : 0 }}>More</span>
      </div>
      {/* Grouped by what the pages are for; the headings show when it's
          open (3.39.2: no lines between groups, and less space) */}
      {[...new Set(tabs.map(t => t.railGroup || 'More'))].sort((a, b) => orderOf(a) - orderOf(b)).map((g, gi) => (
        <div key={g} role="group" aria-label={g} style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <div style={{ ...S.group, ...(gi ? S.groupNext : {}) }} className="rail-muted" aria-hidden="true">
            <span style={{ ...S.label, opacity: open ? 1 : 0 }}>{g}</span>
          </div>
          {tabs.filter(t => (t.railGroup || 'More') === g).map(t => item(t))}
        </div>
      ))}
    </nav>
  );

  function item(t) {
    const on = active === t.id;
    return (
      <a key={t.id} href={hrefFor(t.id)} aria-current={on ? 'page' : undefined} data-tour={`tab-${t.id}`} title={open ? undefined : String(labelFor(t))}
        onClick={e => {
          if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
          e.preventDefault();
          onGo(t.id);
          // 3.47.3: a mouse click doesn't leave the item focused (a later key press would ring it)
          if (e.detail > 0) e.currentTarget.blur();
        }}
        className="rail-item" style={{ ...S.item, ...(on ? S.itemOn : {}) }}>
        <span style={S.icon} aria-hidden="true"><Emo e={t.icon} />{alerts[t.id] && <span style={S.dot} title={alerts[t.id]} />}</span>
        <span style={{ ...S.label, opacity: open ? 1 : 0 }}>{labelFor(t)}{alerts[t.id] && <span style={S.sr}> ({alerts[t.id]})</span>}</span>
      </a>
    );
  }
}

export const RAIL_WIDTH = 60;
// 3.40.0: People & settings first (Settings alone for people who can't open
// Users), then Activity, Super Admin and Resources
const GROUP_ORDER = ['People & settings', 'Settings', 'Activity', 'Super Admin', 'Resources'];
const orderOf = g => { const i = GROUP_ORDER.indexOf(g); return i < 0 ? GROUP_ORDER.length : i; };

const S = {
  rail: { position: 'fixed', left: 0, bottom: 0, zIndex: 95, borderRight: '1px solid var(--b-c8e6c9)', overflowX: 'hidden', overflowY: 'auto',
    paddingTop: 8, transition: 'width .2s cubic-bezier(.2,.8,.2,1), box-shadow .2s', display: 'flex', flexDirection: 'column', gap: 2 },
  hint: { display: 'flex', alignItems: 'center', gap: 12, padding: '4px 18px 4px', color: 'var(--t-90a4ae)', fontSize: 12, fontWeight: 800, letterSpacing: '0.6px', textTransform: 'uppercase', whiteSpace: 'nowrap' },
  chev: { width: 24, textAlign: 'center', fontSize: 16, color: 'var(--t-00897b)' },
  item: { display: 'flex', alignItems: 'center', gap: 10, margin: '0 8px', padding: '7px 8px', borderRadius: 10, textDecoration: 'none', color: 'var(--t-37474f)', fontSize: 14, fontWeight: 600, whiteSpace: 'nowrap', transition: 'background .15s' },
  itemOn: { background: 'linear-gradient(135deg, var(--c-00897b), var(--c-26a69a) 55%, var(--b-43a047))', color: 'var(--t-ffffff)', boxShadow: '0 4px 12px rgb(var(--c-00897b-rgb) / 0.35)' },
  icon: { position: 'relative', width: 28, textAlign: 'center', fontSize: 19, flexShrink: 0 },
  label: { transition: 'opacity .15s' },
  group: { padding: '2px 18px 0', minHeight: 14, fontSize: 10, fontWeight: 800, letterSpacing: '0.7px', textTransform: 'uppercase', color: 'var(--t-90a4ae)', whiteSpace: 'nowrap' },
  groupNext: { marginTop: 6 },
  dot: { position: 'absolute', top: -2, right: -2, width: 9, height: 9, borderRadius: '50%', background: 'var(--c-ef6c00)', border: '2px solid var(--c-ffffff)' },
  sr: { position: 'absolute', width: 1, height: 1, overflow: 'hidden', clip: 'rect(0 0 0 0)' },
};
